import apiFetch from "@wordpress/api-fetch";
import type { APIFetchMiddleware, APIFetchOptions } from "@wordpress/api-fetch";

type WooCommerceApiError = {
	code: string;
	message: string;
	data: {
		status: number;
	};
};

type UnknownError = {
	code?: string;
	message?: string;
	status?: number;
	data?: {
		status?: number;
	};
};

// Stores the current nonce for the middleware.
let currentNonce = "";
let currentTimestamp = 0;

try {
	const storedNonceValue = window.localStorage.getItem("cpops:storeApiNonce");
	const parsedNonce = storedNonceValue
		? (JSON.parse(storedNonceValue) as { nonce: string; timestamp: number })
		: null;
	currentNonce = parsedNonce?.nonce ?? "";
	currentTimestamp = parsedNonce?.timestamp ?? 0;
} catch {
	// We can ignore an error from JSON parse.
}

/**
 * Returns whether or not this is a wc/store API request.
 */
function isStoreApiRequest(options: APIFetchOptions) {
	const url = options.url ?? options.path;

	if (!url) {
		return false;
	}

	if (options.method === "GET") {
		return false;
	}

	return /wc\/store\/v1\//.exec(url) !== null;
}

/**
 * Set the current nonce from a header object.
 */
function setNonce(headers: APIFetchOptions["headers"] | Headers) {
	let nonce: string | null = null;
	let timestamp: string | null = null;

	if (headers instanceof Headers) {
		nonce = headers.get("Nonce");
		timestamp = headers.get("Nonce-Timestamp");
	} else if (typeof headers === "object" && headers !== null) {
		nonce = headers.Nonce;
		timestamp = headers["Nonce-Timestamp"];
	}

	if (nonce) {
		updateNonce(nonce, timestamp);
	}
}

/**
 * Updates the stored nonce within localStorage so it is persisted between page loads.
 */
function updateNonce(nonce: string, timestamp: string | null) {
	// If the "new" nonce matches the current nonce, we don't need to update.
	if (nonce === currentNonce) {
		return;
	}

	// Only update the nonce if newer. It might be coming from cache.
	if (currentTimestamp && timestamp && Number(timestamp) < currentTimestamp) {
		return;
	}

	currentNonce = nonce;
	currentTimestamp = Number(timestamp) ?? Date.now() / 1000; // Convert ms to seconds to match php time()

	// Update the persisted values.
	window.localStorage.setItem(
		"cpops:storeApiNonce",
		JSON.stringify({
			nonce: currentNonce,
			timestamp: currentTimestamp,
		}),
	);
}

function appendNonceHeader(request: APIFetchOptions) {
	const headers = request.headers ?? {};

	request.headers = {
		...headers,
		Nonce: currentNonce,
	};

	return request;
}

const wcStoreApiNonceMiddleware: APIFetchMiddleware = (options, next) => {
	if (isStoreApiRequest(options)) {
		const modifiedOptions = appendNonceHeader(options);

		if (Array.isArray(modifiedOptions?.data?.requests)) {
			modifiedOptions.data.requests = modifiedOptions.data.requests.map(appendNonceHeader);
		}

		return next(modifiedOptions);
	}

	return next(options);
};

/**
 * Middleware to extract and update Store API nonces from response headers.
 * This wraps the fetch call to intercept Response objects before they're parsed.
 */
const nonceResponseMiddleware: APIFetchMiddleware = (options, next) => {
	// Store original parse option
	const originalParse = options.parse ?? true;

	// Tell apiFetch not to auto-parse so we can access raw Response
	options.parse = false;

	const result = next(options);

	return result.then(async (response) => {
		// Check if response is a Response object (from fetch)
		if (response instanceof Response) {
			// Extract and update nonce from headers
			setNonce(response.headers);

			// Check if this is an error response
			if (!response.ok) {
				// For error responses, parse and throw so the error handler gets the parsed data
				const contentType = response.headers.get('content-type');
				let errorData;

				if (contentType?.includes('application/json')) {
					errorData = await response.json();
				} else {
					errorData = await response.text();
				}

				// Throw with the parsed error data
				throw errorData;
			}

			// Now parse successful responses if originally requested
			if (originalParse) {
				const contentType = response.headers.get('content-type');
				const contentLength = response.headers.get('content-length');

				// Check if response has content before attempting to parse
				// Some endpoints (like DELETE) may return empty responses
				if (contentLength === '0' || contentLength === null) {
					// Clone the response to safely check if body is empty
					const clonedResponse = response.clone();
					const text = await clonedResponse.text();

					// If body is empty, return null instead of trying to parse
					if (!text || text.trim() === '') {
						return null;
					}
				}

				if (contentType?.includes('application/json')) {
					return response.json();
				}
				return response.text();
			}
			return response;
		}
		// If not a Response object, return as-is (already parsed upstream)
		return response;
	}).catch(async (error) => {
		// On error, check if there's a response with headers
		if (error && typeof error === 'object') {
			// Try to extract headers from error object
			if ('headers' in error && error.headers instanceof Headers) {
				setNonce(error.headers);
			}
		}
		throw error;
	});
};

const logMiddleware: APIFetchMiddleware = (options, next) => {
	const start = Date.now();
	const result = next(options);

	result
		.then(() => {
			console.info(
				`[api: ${options.method}] ${options.path} ${Date.now() - start}ms`,
			);
		})
		.catch((e) => {
			console.error(
				`[api: ${options.method}] ${options.path} ${Date.now() - start}ms`,
				e,
			);
		});

	return result;
};

// Order matters: nonceResponseMiddleware must be first to intercept raw Response
apiFetch.use(nonceResponseMiddleware);
apiFetch.use(wcStoreApiNonceMiddleware);
apiFetch.use(logMiddleware);

// @see https://github.com/woocommerce/woocommerce-blocks/blob/6cd3ab745be5a07ef486eee9f4f34c1708428154/assets/js/middleware/store-api-nonce.js#L114C1-L114C30
// @ts-expect-error - This is actually available, but not typed in the package.
apiFetch.setNonce = setNonce;

// Initialize Store API nonce from window.leat
// window.leat is always available on frontend pages via the global script
// and contains common data like the Store API nonce
if (window.leat?.wc_store_api_nonce) {
	updateNonce(
		window.leat.wc_store_api_nonce,
		window.leat.wc_store_api_nonce_timestamp?.toString(),
	);
} else {
	console.warn(
		"Leat: No Store API nonce found. Authenticated requests may fail.",
	);
}

export type ApiError = {
	status: number;
	statusText: string;
	data: string;
};

// API Wrapper
async function request<T = unknown>(
	path: string,
	data?: unknown,
	options?: APIFetchOptions,
	method = "GET",
): Promise<{
	data: T | null;
	error: ApiError | null;
}> {
	return apiFetch<T>({ path, method, data, ...options })
		.then((data) => {
			return {
				data,
				error: null,
			};
		})
		.catch(async (e) => {
			// Log the raw error for debugging
			console.log('API request error:', JSON.stringify(e, null, 2));
			console.log('API request error (raw):', e);

			// If error is a Response object, we need to parse it
			if (e instanceof Response) {
				try {
					const errorData = await e.json() as WooCommerceApiError;
					console.log('Parsed error from Response:', errorData);

					return {
						data: null,
						error: {
							status: errorData?.data?.status ?? e.status ?? 500,
							statusText: errorData?.code ?? 'unknown',
							data: errorData?.message ?? 'Unknown error occurred',
						},
					};
				} catch (jsonError) {
					// If JSON parsing fails, use response status text
					return {
						data: null,
						error: {
							status: e.status ?? 500,
							statusText: e.statusText ?? 'unknown',
							data: e.statusText ?? 'Unknown error occurred',
						},
					};
				}
			}

			// Try to extract error information from various possible structures
			const err = e as WooCommerceApiError | UnknownError | null;
			const unknownErr = e as UnknownError;

			// Determine status code
			const status = err?.data?.status ?? unknownErr?.status ?? 500;

			// Determine error code/statusText
			const statusText = err?.code ?? unknownErr?.code ?? "unknown";

			// Determine error message - try multiple possible locations
			const errorMessage = err?.message ?? unknownErr?.message ?? "Unknown error occurred";

			console.log('Parsed error:', { status, statusText, errorMessage });

			return {
				data: null,
				error: {
					status,
					statusText,
					data: errorMessage,
				},
			};
		});
}

/**
 * A simple SDK wrapper around the `@wordpress/api-fetch` package.
 *
 * @see https://github.com/woocommerce/woocommerce-blocks/blob/trunk/src/StoreApi/docs
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-api-fetch
 */
// ... existing code ...
export const api = {
	get: <T = unknown>(path: string, options?: APIFetchOptions) => {
		return request<T>(path, undefined, options, "GET");
	},
	post: <T = unknown>(
		path: string,
		data?: unknown,
		options?: APIFetchOptions,
	) => {
		return request<T>(path, data, options, "POST");
	},
	put: <T = unknown>(
		path: string,
		data?: unknown,
		options?: APIFetchOptions,
	) => {
		return request<T>(path, data, options, "PUT");
	},
	delete: <T = unknown>(path: string, options?: APIFetchOptions) => {
		return request<T>(path, undefined, options, "DELETE");
	},
};
